import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { cn } from '../utlis/cn'
import { motion } from 'framer-motion'
import { carmackClick } from '@/utils'

export const TAB_HEIGHT_PX = 40
const TAB_PADDING_Y_PX = 8
const TAB_PADDING_X_PX = 8
// TODO: Use this to make slope dynamic
const TAB_SLOPE_WIDTH_PX = 50

// Space in pixels between text and badge X axis
const BADGE_OFFSET_PX = 0

// Because I am dynamically calculating tab width based on text content
// - there is a consistent need to offset by 5px in the -X direction to achieve centering
//
// Can possibly be removed if we find the error in my formula
const TEXT_CENTERING_OFFSET_X_PX = -7
const TEXT_CENTERING_OFFSET_Y_PX = 3

type CalculatedSvgTab = Tab & {
    width: number
    priorTabWidthsSum: number
}

// HACK ALERT
// We are building tabs with SVGs because they are complicated from the design provided.
// Thus - I do some hidden svg generating to get the tab widths dynamically.
// This will allow for variable text for the tab data
const generateTabWidths = (tabs: Tab[], containerWidth: number, widthFitTabs?: boolean): CalculatedSvgTab[] => {
    const calculatedTabs: CalculatedSvgTab[] = []

    // Pre-calculate the text widths in descending order so we can proportionally adjust tab widths.
    let textWidths: Array<{ width: number; tabId: string }> = [
        ...tabs.map(({ label, id }) => {
            const measureSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
            measureSvg.style.position = 'absolute'
            measureSvg.style.visibility = 'hidden'
            measureSvg.style.pointerEvents = 'none'
            document.body.appendChild(measureSvg)

            // Add text element to measure
            const measureText = document.createElementNS('http://www.w3.org/2000/svg', 'text')

            measureText.setAttribute('class', 'text-lg')
            measureText.textContent = label
            measureSvg.appendChild(measureText)

            // Get text dimensions
            const textWidth = (measureText as unknown as SVGGraphicsElement).getBBox().width
            // Clean up
            document.body.removeChild(measureSvg)
            return {
                width: textWidth,
                tabId: id,
            }
        }),
    ].sort((a, b) => b.width - a.width)

    let remainingSpace = containerWidth
    let remainingAverageTabWidth = Math.ceil(remainingSpace / textWidths.length)

    const adjustedTextWidths: Array<{ width: number; tabId: string }> = textWidths.map(
        ({ tabId, width }, currentIndex) => {
            if (widthFitTabs) {
                if (width > remainingAverageTabWidth) {
                    // TODO: This needs some slight adjustment if we want text that is wider than the average tab width.
                    // Currently doesn't work well.
                    const fullTabSize = width + 50 // include slope padding
                    remainingSpace = remainingSpace - fullTabSize
                    remainingAverageTabWidth = Math.ceil(remainingSpace / (textWidths.length - currentIndex + 1))
                    return {
                        width: width,
                        tabId,
                    }
                } else {
                    return {
                        width: remainingAverageTabWidth,
                        tabId,
                    }
                }
            } else {
                return {
                    width: Math.ceil(width) + TAB_PADDING_X_PX,
                    tabId,
                }
            }
        }
    )

    const adjustedTabWidthMap: Record<string, number> = Object.fromEntries(
        adjustedTextWidths.map(({ tabId, width }) => [tabId, width])
    )

    for (const [index, tab] of tabs.entries()) {
        const calculatedTab: CalculatedSvgTab = { ...tab, priorTabWidthsSum: 0, width: 0 }
        // The first and last tab have a singular slope, middle tabs have 2 slopes.
        const slopePadding = index > 1 && index < tabs.length - 1 ? TAB_SLOPE_WIDTH_PX * 2 : TAB_SLOPE_WIDTH_PX

        calculatedTab.width = adjustedTabWidthMap[tab.id] - (widthFitTabs ? slopePadding : 0)

        if (index > 0) {
            calculatedTab.priorTabWidthsSum = calculatedTabs.reduce(
                (curr, val, reducerIndex) => (index > reducerIndex ? curr + val.width + TAB_SLOPE_WIDTH_PX : curr + 0),
                0
            )
        }
        calculatedTabs.push(calculatedTab)
    }

    return calculatedTabs
}

// Helper function to generate the full path based on active tab
const generateContainerPath = (
    containerWidth: number,
    containerHeight: number,
    activeTabIndex: number,
    tabs: CalculatedSvgTab[],
    widthFitTabs?: boolean
) => {
    containerHeight = containerHeight + TAB_HEIGHT_PX

    if (widthFitTabs && activeTabIndex === tabs.length - 1) {
        const priorTabsSum = tabs[activeTabIndex].priorTabWidthsSum
        const tabWidth = tabs[activeTabIndex].width
        const slopeUpStart = priorTabsSum - 25
        // When widthFitTabs is enabled, the right-most tab is a mirror of the left most tab.
        // This means a few commands need to do nothing to allow for animations.
        //
        // SVG Explanation:
        // M - start at 25 (x), tab height (y) - x being 25 is used as the end point of the rounded edge of the top left of the container
        //    when the left most tab is not selected
        // L - drawing a line to where the tab should begin sloping up (x), tab height (y)
        //   drawing left rising slope given 50px as the width
        // Q - draw a concave line using a quadractic bezier curve from tab height to the half of the tab height (Y)
        // Q - draw a convex line using a quadractic bezier curve to the top of the tab
        //   drawing top of tab
        // L - draw a line from ending of left tab slope to where the right slope down of the container should begin
        // Q - no effect quadratic curve - points only
        // Q - no effect quadratic curve - points only
        // L - no effect line - points only
        //   draw top right rounded corner
        // Q - draw a convex line using a quadratic bezier curve for the top right corner
        // L - draw a line from right edge of container to the bottom right edge of container - 25px
        // Q - draw a convex line using a quadratic bezier curve for the bottom right corner
        // L - draw a line from bottom right to bottom left corner of container - 25px
        // Q - draw a convex line using a quadratic bezier curve for the bottom left corner
        // L - draw a line defining the left edge up to the point where we want to begin the top left of the corner tab
        // Q - draw a convex line using a quadractic bezier curve for the top left corner of the container
        // Z - close the path
        return `
              M 25 ${TAB_HEIGHT_PX}
              L ${slopeUpStart} ${TAB_HEIGHT_PX}
              Q ${slopeUpStart + 15} ${TAB_HEIGHT_PX} ${slopeUpStart + 25} ${TAB_HEIGHT_PX / 2}
              Q ${slopeUpStart + 35} 0 ${slopeUpStart + 50} 0
              L ${containerWidth - 25} 0
              Q ${containerWidth - 25} 0 ${containerWidth - 25} 0
              Q ${containerWidth - 25} 0 ${containerWidth - 25} 0
              L ${containerWidth - 25} 0
              Q ${containerWidth} 0 ${containerWidth} 25
              L ${containerWidth} ${containerHeight - 25}
              Q ${containerWidth} ${containerHeight} ${containerWidth - 25} ${containerHeight}
              L 25 ${containerHeight}
              Q 0 ${containerHeight} 0 ${containerHeight - 25}
              L 0 ${TAB_HEIGHT_PX * 2 - 25}
              Q 0 ${TAB_HEIGHT_PX} ${TAB_HEIGHT_PX / 2 - 5} ${TAB_HEIGHT_PX}
              Z
            `
    } else if (activeTabIndex === 0) {
        const tabMidpoint = tabs[activeTabIndex].width / 2
        const slopeDownStart = tabs[activeTabIndex].width

        // The left-most tab has a different visual representation than the other tabs.
        // The first 4 commands of this SVG are all doing nothing - but for animations to work correctly
        // with SVGs - you need the same number of points. Thus I fake a bunch of points for animations to succeed.
        //
        // SVG Explanation:
        // M - start at tab midpoint (x), 0 (y)
        // L - "draw a line" to tab midpoint (x), 0 (y)
        // Q - "draw a quadratic bezier curve" that does nothing
        // Q - "draw a quadratic bezier curve" that does nothing
        //   drawing top of tab
        // L - draw a line from midpoint to where the slope down should start (x), 0 (y)
        //   drawing slope down given 50px as the width
        // Q - draw a convex line using a quadratic bezier curve to halfway down the slope
        // Q - draw a concave line using a quadratic bezier curve from halfway to the tab height (Y)
        //   drawing the rest of the container
        // L - draw a line from the ending of the slope to the right edge of the container - 25px
        //   draw top right rounded corner
        // Q - draw a convex line using a quadratic bezier curve for the top right corner
        // L - draw a line from right edge of container to the bottom right edge of container - 25px
        // Q - draw a convex line using a quadratic bezier curve for the bottom right corner
        // L - draw a line from bottom right to bottom left corner of container - 25px
        // Q - draw a convex line using a quadratic bezier curve for the bottom left corner
        // L - draw a line defining the left edge up to halfway the tab height (beginning a rounded top left corner of the tab)
        // Q - draw a convex line using a quadratic bezier curve for the top left of the tab
        // Z - close the path
        return `
          M ${tabMidpoint} 0
          L ${tabMidpoint} 0
          Q ${tabMidpoint} 0 ${tabMidpoint} 0
          Q ${tabMidpoint} 0 ${tabMidpoint} 0
          L ${slopeDownStart} 0
          Q ${slopeDownStart + 15} 0 ${slopeDownStart + 25} ${TAB_HEIGHT_PX / 2}
          Q ${slopeDownStart + 35} ${TAB_HEIGHT_PX} ${slopeDownStart + 50} ${TAB_HEIGHT_PX}
          L ${containerWidth - 25} ${TAB_HEIGHT_PX}
          Q ${containerWidth} ${TAB_HEIGHT_PX} ${containerWidth} ${TAB_HEIGHT_PX + 25}
          L ${containerWidth} ${containerHeight - 25}
          Q ${containerWidth} ${containerHeight} ${containerWidth - 25} ${containerHeight}
          L 25 ${containerHeight}
          Q 0 ${containerHeight} 0 ${containerHeight - 25}
          L 0 ${TAB_HEIGHT_PX / 2}
          Q 0 0 ${TAB_HEIGHT_PX / 2} 0
          Z
        `
    } else {
        const priorTabsSum = tabs[activeTabIndex].priorTabWidthsSum
        const tabWidth = tabs[activeTabIndex].width
        const slopeUpStart = priorTabsSum - 25
        const slopeDownStart = priorTabsSum + tabWidth

        // This SVG is a general tab with a slope on the left and right side that take about 50px of width
        //
        // SVG Explanation:
        // M - start at 25 (x), tab height (y) - x being 25 is used as the end point of the rounded edge of the top left of the container
        //    when the left most tab is not selected
        // L - drawing a line to where the tab should begin sloping up (x), tab height (y)
        //   drawing left rising slope given 50px as the width
        // Q - draw a concave line using a quadractic bezier curve from tab height to the half of the tab height (Y)
        // Q - draw a convex line using a quadractic bezier curve to the top of the tab
        //   drawing top of tab
        // L - draw a line from ending of left tab slope to where the right slope down should start (x), 0 (y)
        //   drawing slope down given 50px as the width
        // Q - draw a convex line using a quadractic bezier curve to halfway down the slope
        // Q - draw a concave line using a quadractic bezier curve from halfway to the tab height (Y)
        //   drawing the rest of the container
        // L - draw a line from the ending of the slope to the right edge of the container - 25px
        //   draw top right rounded corner
        // Q - draw a convex line using a quadratic bezier curve for the top right corner
        // L - draw a line from right edge of container to the bottom right edge of container - 25px
        // Q - draw a convex line using a quadratic bezier curve for the bottom right corner
        // L - draw a line from bottom right to bottom left corner of container - 25px
        // Q - draw a convex line using a quadratic bezier curve for the bottom left corner
        // L - draw a line defining the left edge up to the point where we want to begin the top left of the corner tab
        // Q - draw a convex line using a quadractic bezier curve for the top left corner of the container
        // Z - close the path
        return `
                M 25 ${TAB_HEIGHT_PX}
                L ${slopeUpStart} ${TAB_HEIGHT_PX}
                Q ${slopeUpStart + 15} ${TAB_HEIGHT_PX} ${slopeUpStart + 25} ${TAB_HEIGHT_PX / 2}
                Q ${slopeUpStart + 35} 0 ${slopeUpStart + 50} 0
                L ${slopeDownStart} 0
                Q ${priorTabsSum + 15 + tabWidth} 0 ${priorTabsSum + 25 + tabWidth} ${TAB_HEIGHT_PX / 2}
                Q ${priorTabsSum + 35 + tabWidth} ${TAB_HEIGHT_PX} ${priorTabsSum + 50 + tabWidth} ${TAB_HEIGHT_PX}
                L ${containerWidth - 25} ${TAB_HEIGHT_PX}
                Q ${containerWidth} ${TAB_HEIGHT_PX} ${containerWidth} ${TAB_HEIGHT_PX + 25}
                L ${containerWidth} ${containerHeight - 25}
                Q ${containerWidth} ${containerHeight} ${containerWidth - 25} ${containerHeight}
                L 25 ${containerHeight}
                Q 0 ${containerHeight} 0 ${containerHeight - 25}
                L 0 ${TAB_HEIGHT_PX * 2 - 25}
                Q 0 ${TAB_HEIGHT_PX} ${TAB_HEIGHT_PX / 2 - 5} ${TAB_HEIGHT_PX}
                Z
              `
    }
}

type Tab = {
    id: string
    label: string
    badge?: number
}

type DiagonalTabsProps = {
    className?: string
    childrenByTabIndex: React.ReactNode[]
    containerWidth: number
    containerHeight: number
    tabs: Tab[]
    responsive?: boolean
    /** If true, the tabs will be constrained and fill the width of the container. */
    widthFitTabs?: boolean
    initialIndex?: number
    onTabChange?: (index: number) => void
}

function DiagonalTabs({
    className,
    childrenByTabIndex,
    containerWidth,
    containerHeight,
    tabs,
    responsive,
    widthFitTabs,
    initialIndex,
    onTabChange,
}: DiagonalTabsProps) {
    const [activeTab, setActiveTab] = useState(initialIndex ?? 0)
    const [changingTab, setChangingTab] = useState(false)
    const [height, setHeight] = useState(containerHeight)

    const [contentNode, setContentNode] = useState<HTMLDivElement>()
    const contentRef = useCallback((node: any) => {
        setContentNode(node)
    }, [])

    const calculatedTabs = useMemo(() => {
        return generateTabWidths(tabs, containerWidth, widthFitTabs)
    }, [containerWidth, tabs, widthFitTabs])

    const activeTabPath = useMemo(() => {
        if (calculatedTabs.length === 0) {
            return ''
        }
        return generateContainerPath(containerWidth, height, activeTab, calculatedTabs, widthFitTabs)
    }, [activeTab, calculatedTabs, containerWidth, height, widthFitTabs])

    useEffect(() => {
        if (!responsive) {
            setHeight(containerHeight)
        }
    }, [containerHeight, responsive])

    useEffect(() => {
        if (!responsive || !contentNode) {
            return
        }

        setHeight(Math.round(contentNode.getBoundingClientRect().height))

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === contentNode) {
                    const nextHeight = Math.round(entry.contentRect.height)
                    if (nextHeight !== height) {
                        setHeight(nextHeight)
                    }
                }
            }
        })

        resizeObserver.observe(contentNode)

        return () => {
            resizeObserver.disconnect()
        }
    }, [responsive, activeTab, contentNode])

    const handleTabChange = (index: number) => {
        setChangingTab(true)
        setActiveTab(index)
        setTimeout(() => {
            setChangingTab(false)
        }, 100)
        if (onTabChange) {
            onTabChange(index)
        }
    }

    if (!calculatedTabs.length) return null

    const contentContainerStyle = responsive ? {} : { height: `${height}px` }

    return (
        <div className={className}>
            <svg
                width={containerWidth}
                height={height + TAB_HEIGHT_PX}
                viewBox={`0 -1.5 ${containerWidth} ${height + TAB_HEIGHT_PX + 1.5}`}
            >
                <defs>
                    <filter id="insetShadow">
                        <feGaussianBlur stdDeviation="8" in="SourceAlpha" result="blur" />
                        <feOffset dx="0" dy="0" in="blur" result="offsetBlur" />
                        <feComposite
                            in="SourceGraphic"
                            in2="offsetBlur"
                            operator="arithmetic"
                            k1="0"
                            k2="1"
                            k3="-0.97"
                            k4="0"
                            result="innerShadow"
                        />
                    </filter>
                </defs>

                {/* Main container */}
                <motion.path
                    className="tab-path"
                    d={activeTabPath}
                    fill="rgba(40, 40, 40, 0.4)"
                    filter="url(#insetShadow)"
                    initial={false}
                    animate={{ d: activeTabPath }}
                    transition={changingTab ? { duration: 0.1, ease: 'easeInOut' } : { duration: 0 }}
                />

                {/* Border stroke */}
                <motion.path
                    className="tab-path"
                    d={activeTabPath}
                    fill="rgba(120, 120, 120, 0.2)"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="1"
                    initial={false}
                    animate={{ d: activeTabPath }}
                    transition={changingTab ? { duration: 0.1, ease: 'easeInOut' } : { duration: 0 }}
                />

                {calculatedTabs.map((tab, index) => (
                    <foreignObject
                        key={tab.id}
                        x={tab.priorTabWidthsSum - TEXT_CENTERING_OFFSET_X_PX}
                        height={TAB_HEIGHT_PX}
                        width={
                            tab.width + TAB_PADDING_X_PX + (!widthFitTabs || index < calculatedTabs.length - 1 ? 0 : 20)
                        }
                    >
                        <motion.div
                            role="button"
                            onMouseDown={carmackClick(() => handleTabChange(index))}
                            onKeyDown={(ev) => ev.key === ' ' && handleTabChange(index)}
                            tabIndex={0}
                            className={cn(
                                'cursor-pointer h-full w-full flex justify-center items-center',
                                index === activeTab ? 'font-medium' : 'font-light opacity-70 hover:opacity-100'
                            )}
                        >
                            <span className="relative inline-block">
                                {tab.label}
                                {(tab?.badge ?? 0) > 0 && (
                                    <span className="absolute -top-2 -right-3 flex items-center justify-center w-4 h-4 rounded-full bg-neutral-700 text-white text-[10px] font-light">
                                        {tab.badge}
                                    </span>
                                )}
                            </span>
                        </motion.div>
                    </foreignObject>
                ))}

                <foreignObject
                    y={TAB_HEIGHT_PX}
                    width={containerWidth}
                    height={height}
                    fill="white"
                    className="overflow-hidden"
                >
                    <div ref={contentRef} style={contentContainerStyle} className="overflow-hidden">
                        {childrenByTabIndex[activeTab]}
                    </div>
                </foreignObject>
            </svg>
        </div>
    )
}

export default DiagonalTabs
